import { DE } from './de/index.js';

/**
 * Lightweight gettext-style localisation. The English source text is the
 * key, so untranslated strings fall back to readable English and static
 * server messages (error texts, map taglines) translate by lookup too.
 *
 * Always call `t()` at render time, never when a module-level constant is
 * defined: the language can change while the app is running.
 */
export type Locale = 'en' | 'de';

export const LOCALES: readonly { id: Locale; native: string; english: string; region: string }[] = [
  { id: 'de', native: 'Deutsch', english: 'German', region: 'DE · AT · CH' },
  { id: 'en', native: 'English', english: 'English', region: 'International' },
];

const STORAGE_KEY = 'nightshift.locale.v1';
const DICTIONARIES: Record<Locale, Readonly<Record<string, string>> | null> = { en: null, de: DE };

type Params = Readonly<Record<string, string | number>>;
type Listener = (locale: Locale) => void;

let current: Locale = readStoredLocale() ?? browserLocale();
const listeners = new Set<Listener>();
const missing = new Set<string>();

function readStoredLocale(): Locale | null {
  try {
    const value = typeof localStorage === 'undefined' ? null : localStorage.getItem(STORAGE_KEY);
    return value === 'de' || value === 'en' ? value : null;
  } catch {
    return null;
  }
}

/** Best guess from the browser, used to preselect the language card. */
export function browserLocale(): Locale {
  // Unit tests assert English copy regardless of the machine's locale.
  if (import.meta.env?.MODE === 'test') return 'en';
  const languages = typeof navigator === 'undefined' ? [] : navigator.languages ?? [navigator.language];
  for (const language of languages) {
    const base = language?.toLowerCase().split('-')[0];
    if (base === 'de') return 'de';
    if (base === 'en') return 'en';
  }
  return 'en';
}

/** True once the player picked a language on the first screen. */
export function hasChosenLocale(): boolean {
  return readStoredLocale() !== null;
}

export function locale(): Locale {
  return current;
}

/** BCP 47 tag for Intl formatters. */
export function intlLocale(): string {
  return current === 'de' ? 'de-DE' : 'en-GB';
}

/** `persist: false` previews a language without counting it as chosen. */
export function setLocale(next: Locale, options: { persist?: boolean } = {}): void {
  if (options.persist !== false) {
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // Private mode: the choice still applies for this session.
    }
  }
  if (next === current) return;
  current = next;
  applyDocumentLocale();
  for (const listener of listeners) listener(next);
}

export function onLocaleChange(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function applyDocumentLocale(): void {
  if (typeof document === 'undefined') return;
  document.documentElement.lang = current;
}

/**
 * Translates English source text. `{name}` placeholders are filled from
 * `params` after lookup, so a translation can reorder them freely.
 */
export function t(english: string, params?: Params): string {
  const dictionary = DICTIONARIES[current];
  let text = english;
  if (dictionary) {
    const translated = dictionary[english];
    if (translated !== undefined) text = translated;
    else if (english.trim() && !missing.has(english)) missing.add(english);
  }
  if (!params) return text;
  return text.replace(/\{(\w+)\}/g, (match, key: string) => (key in params ? String(params[key]) : match));
}

/**
 * Translates a word whose meaning depends on where it is shown, e.g. the
 * CLEAR weather chip vs. the CLEAR button. Looks up `context|english` first.
 */
export function tc(context: string, english: string, params?: Params): string {
  const dictionary = DICTIONARIES[current];
  const contextual = dictionary?.[`${context}|${english}`];
  if (contextual === undefined) return t(english, params);
  return params ? t(contextual, params) : contextual;
}

/** Picks the singular or plural source text before translating it. */
export function tn(count: number, singular: string, plural: string, params?: Params): string {
  return t(count === 1 ? singular : plural, { n: count, ...params });
}

const numberFormatters = new Map<string, Intl.NumberFormat>();
const MAX_NUMBER_FORMATTERS = 32;

/** Locale-aware number formatting (thousands separators, decimals). */
export function formatNumber(value: number, options?: Intl.NumberFormatOptions): string {
  // Sort option keys so equivalent callers share a formatter regardless of
  // object construction order. Keep the cache bounded for dynamic callers.
  const key = options
    ? JSON.stringify([intlLocale(), Object.entries(options).sort(([a], [b]) => a.localeCompare(b))])
    : intlLocale();
  let formatter = numberFormatters.get(key);
  if (!formatter) {
    formatter = new Intl.NumberFormat(intlLocale(), options);
    if (numberFormatters.size >= MAX_NUMBER_FORMATTERS) numberFormatters.clear();
    numberFormatters.set(key, formatter);
  }
  return formatter.format(value);
}

/** Dev aid: strings looked up this session without a translation. */
export function missingTranslations(): string[] {
  return [...missing].sort();
}

applyDocumentLocale();
if (import.meta.env?.DEV && typeof window !== 'undefined') {
  (window as unknown as Record<string, unknown>).__nightshiftI18n = { missingTranslations, setLocale, locale };
}
